import { ImageIcon, Loader2, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PaginationBar } from '@/components/data-grid';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/app/routes';
import type { PostResponseDTO } from '@/api/generated/model';
import { useUserTopContent } from '../api';
import { formatCardDate, postThumbnailSrc } from '../lib/media';

/** True when the post is hidden or terminated. */
function isHidden(post: PostResponseDTO): boolean {
  const s = (post.postStatus ?? '').trim().toUpperCase();
  return s === 'HID' || s === 'HIDDEN' || s === 'TER' || s === 'TERMINATED';
}

function AllContentCard({ post, onClick }: { post: PostResponseDTO; onClick: () => void }) {
  const src = postThumbnailSrc(post);
  const images = post.imageCount ?? 0;
  const videos = post.videoCount ?? 0;
  const count = images + videos;
  const hidden = isHidden(post);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-[9/16] overflow-hidden rounded-lg bg-muted text-left transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {src ? (
        <img
          src={src}
          alt=""
          className={cn('size-full object-cover', hidden && 'blur-[2px] grayscale')}
          loading="lazy"
        />
      ) : (
        <div className="flex size-full items-center justify-center text-muted-foreground">
          <ImageIcon className="size-6" />
        </div>
      )}
      {post.createdAt && (
        <span className="absolute left-1 top-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
          {formatCardDate(post.createdAt)}
        </span>
      )}
      {hidden && (
        <span className="absolute right-1 top-1 rounded bg-red-600/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
          Hidden
        </span>
      )}
      {count > 0 && (
        <div className="absolute bottom-1 left-1 flex items-center gap-1 text-white drop-shadow">
          {images > 0 && <ImageIcon className="size-4" />}
          {videos > 0 && <Video className="size-4" />}
          <span className="text-xs">{count}</span>
        </div>
      )}
    </button>
  );
}

// User detail "All Content" media grid (op #6). 9:16 cards, paged, reset on userId.
// Cards navigate to the content detail route on click (ROUTES.contentDetails).
export function UserAllContent({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const table = useUserTopContent(userId);

  return (
    <Card id="all-content">
      <CardHeader>
        <CardTitle className="text-sm">All Content</CardTitle>
      </CardHeader>
      <CardContent>
        {table.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : table.isError ? (
          <div className="space-y-2 py-6 text-center">
            <p className="text-sm text-destructive">Failed to load content.</p>
            <Button size="sm" variant="outline" onClick={() => void table.refetch()}>
              Retry
            </Button>
          </div>
        ) : table.rows.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No content.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
              {table.rows.map((p, i) => (
                <AllContentCard
                  key={p.id ?? i}
                  post={p}
                  onClick={() => {
                    if (p.id != null) void navigate(ROUTES.contentDetails(String(p.id)));
                  }}
                />
              ))}
            </div>
            <PaginationBar
              page={table.page}
              totalPage={table.totalPage}
              onPageChange={table.setPage}
              isFetching={table.isFetching}
              className="mt-2 border-t-0 pb-0"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
